import { css, StyleSheet } from 'aphrodite';
import { format } from 'date-fns';
import * as React from 'react';
import { Button } from '../../../components-v2/Button';
import { THEME } from '../../../components-v2/Theme/theme';
import { PlayIcon } from '../../../components/svgs/icons/PlayIcon';

interface IProps {
	now: Date;
	overdueCount: number;
	todayCount: number;
	tomorrowCount: number;
	focusCount: number;
	/** What the queue holds: "today", "selected", or a bucket label such as "tomorrow". */
	focusScope?: string;
	/** True until the first page of action items has loaded. */
	isLoading?: boolean;
	onStartFocus: () => void;
}

function focusTitle(scope: string) {
	const what = scope === 'selected' ? 'the selected' : scope === 'today' ? 'overdue and today’s' : `${scope}’s`;
	return `Work through ${what} items one at a time, with each contact’s details alongside`;
}

function plural(count: number, singular: string, pluralForm = `${singular}s`) {
	return `${count} ${count === 1 ? singular : pluralForm}`;
}

export function buildDayStatus({
	overdueCount,
	todayCount,
	tomorrowCount,
}: Pick<IProps, 'overdueCount' | 'todayCount' | 'tomorrowCount'>): string {
	if (overdueCount === 0 && todayCount === 0 && tomorrowCount === 0) {
		return 'Nothing scheduled. Enjoy the open runway.';
	}
	const parts: string[] = [];
	if (overdueCount > 0) {
		parts.push(`${overdueCount} overdue`);
	}
	parts.push(`${todayCount} due today`);
	if (tomorrowCount > 0) {
		parts.push(`${plural(tomorrowCount, 'item')} tomorrow`);
	}
	return parts.join(' · ');
}

export const DayHeader: React.FC<IProps> = ({
	now,
	overdueCount,
	todayCount,
	tomorrowCount,
	focusCount,
	focusScope = 'today',
	isLoading = false,
	onStartFocus,
}) => {
	return (
		<div
			className={css(styleSheet.container, overdueCount > 0 && styleSheet.containerOverdue)}
			data-testid='day-header'
		>
			<div className={css(styleSheet.text)}>
				<span className={css(styleSheet.eyebrow)}>{format(now, 'EEEE')}</span>
				<h2 className={css(styleSheet.date)}>{format(now, 'MMMM d')}</h2>
				<span className={css(styleSheet.status)}>
					{isLoading ? 'Loading your action items…' : buildDayStatus({ overdueCount, todayCount, tomorrowCount })}
				</span>
			</div>
			<Button
				variant='primary'
				disabled={isLoading || focusCount === 0}
				onClick={onStartFocus}
				title={isLoading ? 'Loading…' : focusCount === 0 ? 'Nothing to focus on right now' : focusTitle(focusScope)}
				styles={[styleSheet.focus]}
			>
				<PlayIcon width={14} height={14} />
				<span>{focusCount > 0 ? `Focus · ${focusCount} ${focusScope}` : 'Focus'}</span>
			</Button>
		</div>
	);
};

const styleSheet = StyleSheet.create({
	container: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: THEME.spacing.md,
		padding: `${THEME.spacing.md}px ${THEME.spacing.lg}px`,
		borderRadius: THEME.common.borderRadius,
		background: `linear-gradient(135deg, ${THEME.colors.primary.light} 0%, ${THEME.colors.background.light} 70%)`,
		border: THEME.common.border,
		borderLeft: `4px solid ${THEME.colors.primary.main}`,
		[THEME.queries.sm]: {
			flexDirection: 'column',
			alignItems: 'flex-start',
		},
	},
	containerOverdue: {
		borderLeftColor: THEME.colors.danger.main,
	},
	text: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		minWidth: 0,
	},
	eyebrow: {
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
		textTransform: 'uppercase',
		letterSpacing: 0.6,
	},
	date: {
		margin: 0,
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.xl,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		lineHeight: 1.2,
	},
	status: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},
	focus: {
		flexShrink: 0,
	},
});
